import React from 'react';

export const PAGE_SIZE_OPTIONS = [50, 100, 200, 'all'];
export const DEFAULT_PAGE_SIZE = 50;

/**
 * Resolve a page-size setting to a concrete row count.
 * @param {number|'all'} pageSize
 * @param {number} totalItems
 */
export function resolvePageSize(pageSize, totalItems) {
  if (pageSize === 'all') return Math.max(totalItems, 1);
  const size = Number(pageSize);
  return Number.isFinite(size) && size > 0 ? size : DEFAULT_PAGE_SIZE;
}

/**
 * Slice filtered rows for the current page.
 */
export function paginateItems(items, currentPage, pageSize) {
  const totalItems = items.length;
  const effectiveSize = resolvePageSize(pageSize, totalItems);
  const totalPages = Math.max(1, Math.ceil(totalItems / effectiveSize) || 1);
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (safePage - 1) * effectiveSize;
  const endIndex = Math.min(startIndex + effectiveSize, totalItems);

  return {
    pageItems: items.slice(startIndex, endIndex),
    totalItems,
    totalPages,
    currentPage: safePage,
    startIndex: totalItems === 0 ? 0 : startIndex + 1,
    endIndex,
    effectiveSize
  };
}

/**
 * Shared table pagination bar: Previous/Next + rows-per-page (50/100/200/All).
 */
export default function TablePagination({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  itemLabel = 'items'
}) {
  const { totalPages, startIndex, endIndex } = paginateItems(
    Array.from({ length: totalItems }),
    currentPage,
    pageSize
  );

  if (totalItems === 0) return null;

  return (
    <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-600 pt-4">
      <div className="flex flex-wrap items-center gap-3">
        <span>
          Showing <span className="font-medium text-gray-800 dark:text-gray-200">{startIndex}</span>
          {'–'}
          <span className="font-medium text-gray-800 dark:text-gray-200">{endIndex}</span>
          {' of '}
          <span className="font-medium text-gray-800 dark:text-gray-200">{totalItems}</span>
          {' '}{itemLabel}
        </span>

        <label className="flex items-center gap-2">
          <span className="whitespace-nowrap">Rows per page</span>
          <select
            value={pageSize === 'all' ? 'all' : String(pageSize)}
            onChange={(e) => {
              const value = e.target.value === 'all' ? 'all' : Number(e.target.value);
              onPageSizeChange(value);
            }}
            className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <option key={String(option)} value={option === 'all' ? 'all' : String(option)}>
                {option === 'all' ? 'All' : option}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage <= 1}
          className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-500 rounded-md px-3 py-1.5 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>
        <span className="min-w-[7rem] text-center">
          Page <span className="font-medium text-gray-800 dark:text-gray-200">{currentPage}</span>
          {' of '}
          <span className="font-medium text-gray-800 dark:text-gray-200">{totalPages}</span>
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage >= totalPages}
          className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-500 rounded-md px-3 py-1.5 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
}
